// Signature types for document signing flow

import type { RepresentativeSigning } from '@mes-amis/super-form';

export interface Signatory {
  id: number;
  first_name: string;
  last_name: string;
  official_full_name?: string;
}

export interface SignatureTitle {
  id: number;
  name: string;
  label: string;
}

/** Who provided the Client/Legal Representative signature — mirrors the
 *  Signing::Signature#client_signature_representative enum in monami. */
export type ClientSignatureRepresentative = 'myself' | 'on_behalf_client' | 'legal_rep' | 'on_behalf_legal_rep';

export interface Signature {
  id?: number;              // Server ID - only set after sync, undefined until then
  /** Storage key: "<document_id>:<slot>" where slot is "title-{id}" / "person-{id}".
   *  On the wire (API payloads and sync requests) local_id is the BARE slot —
   *  createSignatures scopes it by document before anything reaches Dexie. */
  local_id: string;
  document_id: number | string; // Document ID (number for synced, UUID string for drafts)
  status: 'requested' | 'completed';
  name_confirmation?: string;
  signed_at?: string;
  completed_at?: string;
  signature_format?: 'typed' | 'file';
  signatory?: Signatory;
  signatory_id?: number;
  signing_request_id?: number;
  title?: SignatureTitle;
  procurator?: Signatory | null;
  client_signature_representative?: ClientSignatureRepresentative;
  signing_statement?: string | null;
  representative_signing?: RepresentativeSigning | null;
  local_completed?: boolean;  // Marked complete locally (id presence indicates if synced)
}

// Type for submitting a signature to the API
export interface SignatureSubmission {
  name_confirmation: string;
  signed_at: string;
  signatory_id: number;
  signature_format: 'typed' | 'file';
  client_signature_representative?: ClientSignatureRepresentative;
  signing_statement?: string | null;
}

// Constants for signature local_id prefixes
export const SIGNATURE_ID_PREFIX = {
  TITLE: 'title-',
  PERSON: 'person-',
} as const;

// Separator between the document scope and the slot in a stored local_id.
// Never appears in document ids (numeric or "draft-<uuid>") or slot ids.
const LOCAL_ID_SCOPE_SEPARATOR = ':';

/**
 * Slot identity of a signature within its document: "title-{id}" / "person-{id}".
 * Deterministic on both sides — the client derives it from the template's
 * signature_config, the server from the signature row — making it the join key
 * between rows the two sides created independently of each other.
 * A signed title slot has both title and signatory; title wins. Person slots
 * arrive from the server with the person as `signatory` (there is no `person` key).
 */
export function getSignatureSlotId(sig: { title?: { id: number } | null; person?: { id: number } | null; signatory?: { id: number } | null }): string | null {
  if (sig.title?.id) return `${SIGNATURE_ID_PREFIX.TITLE}${sig.title.id}`;
  if (sig.person?.id) return `${SIGNATURE_ID_PREFIX.PERSON}${sig.person.id}`;
  if (sig.signatory?.id) return `${SIGNATURE_ID_PREFIX.PERSON}${sig.signatory.id}`;
  return null;
}

/**
 * Storage key for a signature row: the slot identity scoped by its document.
 * The slot alone is only unique within one document — unscoped it collides
 * across documents sharing a signature config (local_id is the Dexie primary key).
 */
export function scopedLocalId(documentId: number | string, slotId: string): string {
  return `${documentId}${LOCAL_ID_SCOPE_SEPARATOR}${slotId}`;
}

/** Bare slot identity from a local_id — tolerates already-bare values (wire
 *  payloads and pre-v42 legacy rows are never scoped). */
export function slotIdFromLocalId(localId: string): string {
  const separatorIndex = localId.lastIndexOf(LOCAL_ID_SCOPE_SEPARATOR);
  return separatorIndex === -1 ? localId : localId.slice(separatorIndex + 1);
}

/**
 * Get a display label for a signature based on its title or person.
 */
export function getSignatureLabel(sig: { title?: { label: string }; person?: { first_name: string; last_name: string } }): string {
  if (sig.title?.label) return sig.title.label;
  if (sig.person) return `${sig.person.first_name} ${sig.person.last_name}`;
  return 'Signature';
}

/**
 * Display order for signature slots — matches the online app, which renders
 * slots by server id ascending (Signing::Signature::Props.collection orders
 * by :id; ids follow creation order: required_titles in config order, then
 * optional_titles). Slots built offline have no server id yet and fall back
 * to `slotOrder` (template config order, titles then people — the sequence
 * the server will create them in). Rows with a server id sort before rows
 * without one so a partially-synced set stays stable.
 */
export function compareSignatureSlots(
  a: Pick<Signature, 'id' | 'local_id'>,
  b: Pick<Signature, 'id' | 'local_id'>,
  slotOrder: Map<string, number>
): number {
  if (a.id != null && b.id != null) return a.id - b.id;
  if (a.id != null || b.id != null) return a.id != null ? -1 : 1;
  return (slotOrder.get(slotIdFromLocalId(a.local_id)) ?? Number.MAX_SAFE_INTEGER)
    - (slotOrder.get(slotIdFromLocalId(b.local_id)) ?? Number.MAX_SAFE_INTEGER);
}
